/**
 * incidentFields.js — shared logic for the treatment/code-specific "incident
 * details" fields (the FIELD_META requirement extras: laser serial number,
 * skin type, training date, injector acknowledgement, …).
 *
 * Drives BOTH the case-file display and its edit pop-up off the SAME source the
 * wizard uses (getRequirements + FIELD_META), so submission↔case-file parity
 * holds for every treatment/code/subtype combination by construction.
 * See submission_vs_case_logic.md and required_evidence_logic.md §6.
 */
import { getRequirements, FIELD_META } from './eventVocabularies.js';

// type_specific keys owned by Near-Miss / Clinic-Incident (rendered by their
// own dedicated UI) — never surface these as Treatment incident-extras.
const NON_TREATMENT_TS_KEYS = new Set([
  'reportingFunction', 'cause', 'correctiveAction', 'assignee', 'targetResolutionDate', 'potentialSeverity',
  'personStatus', 'injuredName', 'injuredContact', 'injuryNature', 'immediateResponse',
  'witnessName', 'witnessContact', 'witnessStatement',
  'refusalRequestedAt', 'refusalBuyerDescription', 'refusalActionTaken',
]);

// type_specific keys that are structures / declarations with their own UI, not
// incident fields: the per-case requirements array, the requirement waivers
// map (lib/waivers.js — rendered by the document slot), and the legacy PR #65
// consent flag that getWaivers() converts into a waiver.
const STRUCTURAL_TS_KEYS = new Set(['requirements', 'waivers', 'no_consent_form']);

const isEmpty = (v) => v == null || v === '';

/**
 * The ordered list of incident-specific field keys for a Treatment case:
 *   1. every field the matrix REQUIRES for (treatment, code) — included even
 *      when empty, so gaps are visible; then
 *   2. every OPTIONAL field the matrix offers for (treatment, code) — also
 *      included when empty (the edit modal must offer them); then
 *   3. any extra non-empty key already stored in type_specific that the matrix
 *      doesn't list — so nothing captured at submission is ever orphaned.
 * Use isOptionalIncidentField() to tell 2 from 1 when rendering.
 */
export function incidentFieldKeys(treatment, code, typeSpecific) {
  const req = getRequirements(treatment, code);
  const keys = [...(req?.fields || [])];
  for (const k of (req?.optionalFields || [])) if (!keys.includes(k)) keys.push(k);
  const ts = typeSpecific && typeof typeSpecific === 'object' ? typeSpecific : {};
  for (const k of Object.keys(ts)) {
    if (STRUCTURAL_TS_KEYS.has(k) || NON_TREATMENT_TS_KEYS.has(k)) continue;
    if (keys.includes(k) || isEmpty(ts[k])) continue;
    keys.push(k);
  }
  return keys;
}

/** True when the matrix lists `key` as OPTIONAL for (treatment, code). */
export function isOptionalIncidentField(treatment, code, key) {
  return (getRequirements(treatment, code)?.optionalFields || []).includes(key);
}

/** Humanise a key with no FIELD_META entry (fallback label). */
function humanize(k) {
  return String(k)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
}

/** Display label for an incident field key. */
export function incidentFieldLabel(key) {
  return FIELD_META[key]?.label || humanize(key);
}

const isTrue  = (v) => v === true  || v === 'true';
const isFalse = (v) => v === false || v === 'false';

/**
 * Format a stored incident-field value for display. `formatDate` is injected so
 * this module stays free of any date-lib choice. Returns null for empty (the
 * caller renders the "Not provided" placeholder) — EXCEPT for a 'yesno' field,
 * which always has a display value ('Yes' | 'No' | 'Not answered').
 */
export function formatIncidentFieldValue(key, value, { formatDate } = {}) {
  const type = FIELD_META[key]?.type;
  if (type === 'yesno') {
    if (isTrue(value))  return 'Yes';
    if (isFalse(value)) return 'No';
    return 'Not answered';
  }
  if (isEmpty(value)) return null;
  if (type === 'checkbox') return isTrue(value) ? 'Yes' : 'No';
  if (type === 'datetime-local') {
    const [d, t] = String(value).split('T');
    const datePart = formatDate ? formatDate(d) : d;
    return t ? `${datePart}, ${t}` : datePart;
  }
  if (type === 'date') return formatDate ? formatDate(value) : String(value);
  if (type === 'textarea') return String(value);
  return String(value);
}

/** How many matrix-REQUIRED fields for (treatment, code) are still outstanding.
 *  Optional fields never count. A checkbox (e.g. an acknowledgement) counts as
 *  outstanding until it is ticked — an explicit "No" (false) is still
 *  outstanding, same as leaving it blank. A yes/no switch is outstanding only
 *  while unanswered — "No" is an answer. */
export function outstandingIncidentFields(treatment, code, typeSpecific) {
  const applicable = getRequirements(treatment, code)?.fields || [];
  const ts = typeSpecific && typeof typeSpecific === 'object' ? typeSpecific : {};
  return applicable.filter(k => {
    const v = ts[k];
    const type = FIELD_META[k]?.type;
    if (type === 'checkbox') return !isTrue(v);
    if (type === 'yesno')    return !isTrue(v) && !isFalse(v);
    return isEmpty(v);
  }).length;
}
